/** Mirrors signaling/src/protocol.ts and SignalMessage.kt. See docs/protocol.md. */
export interface SignalMessage {
  type: string;
  sessionId?: string;
  pin?: string;
  sdp?: string;
  sdpType?: 'offer' | 'answer';
  candidate?: string;
  sdpMid?: string;
  sdpMLineIndex?: number;
  reason?: string;
  /** ICE configuration pushed by the server once this client is admitted. */
  iceServers?: RTCIceServer[];
  /** 'relay' forces TURN, used to prove the relay path actually works. */
  iceTransportPolicy?: RTCIceTransportPolicy;

  // --- Unattended access. The viewer only ever sets `code`; the rest are here so
  // the shared shape stays in sync with the server and Android sides.
  deviceId?: string;
  secret?: string;
  code?: string;
  fcmToken?: string;
}

export const MessageType = {
  HOST: 'host',
  JOIN: 'join',
  HOSTED: 'hosted',
  JOINED: 'joined',
  PEER_JOINED: 'peer-joined',
  PEER_LEFT: 'peer-left',
  SIGNAL: 'signal',
  ERROR: 'error',

  // --- Unattended access. The viewer sends CONNECT_DEVICE and may receive WAKING
  // before the usual JOINED; the rest are device/server only. See docs/protocol.md.
  CONNECT_DEVICE: 'connect-device',
  WAKING: 'waking',
} as const;

/** Viewer to host, over the `input` data channel. */
export interface InputCommand {
  t: 'pointer' | 'scroll' | 'key' | 'text';
  a?: string;
  /** Normalized to [0,1] against the video frame, never pixels. */
  x?: number;
  y?: number;
  x2?: number;
  y2?: number;
  v?: string;
  d?: number;
}

const ERROR_TEXT: Record<string, string> = {
  'bad-pin': 'Wrong PIN.',
  'no-such-session': 'No session with that code. Check the phone.',
  'session-taken': 'That code is already in use.',
  'room-full': 'Someone else is already connected to this session.',
  'rate-limited': 'Too many attempts. Reload and try again.',
  malformed: 'The server rejected the request.',
  // Unattended access. `bad-secret` is device-only, so the viewer never sees it.
  'no-such-device': 'No device with that code is enrolled.',
  'device-unreachable': 'The device is offline and could not be reached.',
  // Enrollment only; a viewer never sees this, but the table mirrors the server's reasons.
  'code-taken': 'That device code is already registered with a different PIN.',
};

export function describeError(reason: string | undefined): string {
  return (reason && ERROR_TEXT[reason]) ?? 'Could not connect.';
}
